import { IRevision } from "../@types/document";
import { IProductCrm } from "../@types/product";

/**
 * Format bytes as human-readable text.
 *
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 *
 * @return Formatted string.
 */
export function humanFileSize(
	bytes: number,
	si: boolean = true,
	dp: number = 1,
) {
	const thresh = si ? 1000 : 1024;

	if (Math.abs(bytes) < thresh) {
		return bytes + " B";
	}

	const units = si
		? ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
		: ["KiB", "MiB", "GiB", "TiB", "PiB", "EiB", "ZiB", "YiB"];
	let u = -1;
	const r = 10 ** dp;

	do {
		bytes /= thresh;
		++u;
	} while (
		Math.round(Math.abs(bytes) * r) / r >= thresh &&
		u < units.length - 1
	);

	return bytes.toFixed(dp) + " " + units[u];
}

export function parseZodErrors(errors: string, t: any): string {
	try {
		const parsed_errors: {
			code: string;
			path: string[];
			message: string;
		}[] = JSON.parse(errors);

		let message = "";
		for (const error of parsed_errors) {
			message += `${t(
				`errors.${error.path?.length > 0
					? error.path?.join(" ")
					: error?.message?.replaceAll(".", "")
				}: ${error.message?.replaceAll(".", "")}`,
			)}<br>`;
		}
		return message;
	} catch (error) {
		try {
			const parsed_errors: string[] = JSON.parse(errors);
			let message = "";
			for (const error of parsed_errors) {
				message += `${t(`errors.${error}`)}<br>`;
			}
			return message;
		} catch (error) {
			return `${t(`errors.${errors}`)}<br>`;
		}
	}
}

/**
 * Returns the file extension of a given filename.
 *
 * If the filename does not have an extension, an empty string is returned.
 *
 * @param {string} filename - The filename to get the extension from
 * @returns {string} The file extension
 */
export function getRevisionFileExtension(filename: string): string {
	return filename?.split(".")[filename?.split(".").length - 1];
}

/**
 * Removes the extension from a given filename.
 *
 * If the filename does not have an extension, the original filename is returned.
 *
 * @param {string} filename - The filename to remove the extension from
 * @returns {string} The filename without its extension
 */
export function removeExtension(filename: string): string {
	const name = filename?.split(".");
	name?.pop();

	return name?.join(".") ?? filename;
}

/**
 * Sorts the given revisions in the following order:
 * 1. By Internal Revision Date DESC
 * 2. By Manual Revision Number DESC
 * 3. By Automatic Revision Number DESC
 * 4. Published revisions first
 *
 * @param {IRevision[]} revisions The revisions to sort. If undefined, returns an empty array.
 * @returns {IRevision[]} The sorted revisions.
 */
export function sortRevisionsByManualRevisionNumber(
	revisions: IRevision[],
): IRevision[] {
	return revisions.sort((a, b) => {
		if (a.internalRevisionDate > b.internalRevisionDate) {
			return -1
		}
		if (a.internalRevisionDate < b.internalRevisionDate) {
			return 1
		}

		const manualRevisionNumberDifference =
			parseInt(b.manualRevisionNumber.replace("R", "")) -
			parseInt(a.manualRevisionNumber.replace("R", ""));

		if (manualRevisionNumberDifference !== 0) {
			// Sort by Manual Revision Number DESC
			return manualRevisionNumberDifference;
		}

		// First the published revisions
		if (a.status === "published" && b.status !== "published") {
			return -1;
		}
		if (b.status === "published" && a.status !== "published") {
			return 1;
		}

		// Sort by Automatic Revision Number DESC
		return (
			parseInt(b.automaticRevisionNumber) -
			parseInt(a.automaticRevisionNumber)
		);
	});
}

/**
 * Retrive the most recent published revision before end of life of product
 *
 * @param {IRevision[]} revisions The revisions to check
 * @param {IProductCrm} product The product to check
 * @returns {IRevision} The most recent published revision before end of life of product.
 */
export function findLastValidPublishedRevision(
	revisions: IRevision[],
	product: IProductCrm
): IRevision | null {
	return revisions.find(r => r.status === "published" && product.data_fine_costruzione && product.data_fine_costruzione >= r.validityStartDate && product.data_fine_costruzione <= r.validityEndDate) ?? null
}

export function thereIsAnotherPublishedRevision(
	currentRevisionAutomaticRevisionNumber: string,
	revisions: IRevision[],
): boolean {
	const revisionsNoCurrentRevision = revisions.filter(
		(r) =>
			r.automaticRevisionNumber !==
			currentRevisionAutomaticRevisionNumber,
	);

	return revisionsNoCurrentRevision.some((r) => r.status === "published");
}

export function removeLocaleTagFromPathname(pathname: string): string {
	return `/${pathname?.split("/")?.splice(2)?.join("/")}`;
}

export function getLastPathOfPathname(pathname: string): string {
	return pathname.split("/").pop() ?? "";
}
